import React, { useContext, useEffect, useRef, useState } from "react";
import { DialogTrigger } from "../../../../components/Dialog/Dialog";
import Item from "../../../../components/Lists/Item";
import { usePermissions } from "../../../../hooks/usePermissions";
import DeleteTypeSeriesDialog from "./DeleteTypeSeriesDialog";
import { LABELS } from "../../../../globals/globals";
import {
  AircraftConfigurationContext,
  AircraftConfigurationProvider,
} from "../../contexts/AircraftConfigurationContext";
import HierarchyLines from "../../../../TestLine/HierarchyLines";
import AircraftForm from "./AircraftForm";
import AircraftFlightStatus from "./AircraftFlightStatus";
import { DropdownsContext } from "../../../../contexts/DropdownsContext";
import DeleteAircraftDialog from "./DeleteAircraftDialog";
import AircraftSeatConfiguration from "./AircraftSeatConfiguration";
import {
  AircraftDeliveryInfoContext,
  AircraftDeliveryInfoProvider,
} from "../../contexts/AircraftDeliveryInfoContext";
import AircraftDeliveryInformation from "./AircraftDeliveryInformation";
import AircraftWarrantyConfiguration from "./AircraftWarrantyConfiguration";
import {
  AircraftWarrantyContext,
  AircraftWarrantyProvider,
} from "../../contexts/AircraftWarrantyContext";
import {
  AircraftBalanceContext,
  AircraftBalanceProvider,
} from "../../contexts/AircraftBalanceContext";
import AircraftBalanceConfiguration from "./AircraftBalanceConfiguration";
import { Sorting } from "../../../../components/Lists/Sorting";
import { Tooltip } from "@mui/material";
import { DialogContext } from "../../../../contexts/DialogContext";

const localStoragePrefix = "configuration/aircraft/generalInfo/draft";

const mapper = {
  tailNumber: "Tail Number",
  registrationNumber: "Registration Number",
  serialNumber: "AC Serial Number",
  lineNumber: "Line Number",
};

const AircraftList = ({ updateInfo, typeSeriesId, numberOfEngines, numberOfLandingGears }) => {
  
  const {
    idxList,
    isAircraftDraft,
    deleteAircraft,
    requestNextPage,
    aircraftList,
    loadingAircraft,
    isAircraftSectionDraft,
    newAircraft,
    setSortingOrder,
    listChange,
  } = useContext(AircraftConfigurationContext);

  const { isDeliverySectionDraft, isDeliveryDraft } = useContext(
    AircraftDeliveryInfoContext
  );
  const { isWarrantySectionDraft, isWarrantyDraft } = useContext(
    AircraftWarrantyContext
  );
  const { isBalanceSectionDraft, isBalanceDraft } = useContext(
    AircraftBalanceContext
  );
  const listRef = useRef(null);
  const doNothing = () => {};
  const { hasPermission } = usePermissions();
  const [openStates, setOpenStates] = useState({});
  const [isDeleteAction, setDeleteAction] = useState(false);

  const { openDialog, closeDialog } = useContext(DialogContext);
  const didMount = useRef(false);
  useEffect(() => {
    if (
      localStorage.getItem(localStoragePrefix + "/new-auto-open") === "true"
    ) {
      toggleOpenState(newAircraft.id);
      localStorage.removeItem(localStoragePrefix + "/new-auto-open");
    }
  }, [newAircraft]);

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 3) <= clientHeight) {
      if (!loadingAircraft) requestNextPage();
    }
  };

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true; // Evita la primera ejecución
      return;
    }
    updateInfo(idxList, typeSeriesId, isDeleteAction);
    setDeleteAction(false);
  }, [listChange]);

  const deleteAircraftAction = (aircraftId) => {
    setDeleteAction(true);
    deleteAircraft(aircraftId);
  };

  const toggleOpenState = (aircraftId) => {
    closeAllOpenStates();
    setTimeout(() => {
      setOpenStates((prevState) => ({
        ...prevState,
        [aircraftId]: !openStates[aircraftId],
      }));
    }, 100);
  };

  useEffect(() => {}, []);

  const closeAllOpenStates = () => {
    setOpenStates((prevState) => {
      const newState = {};
      Object.keys(prevState).forEach((key) => {
        newState[key] = false;
      });
      return newState;
    });
  };
  const isEnabled = (section, aircraft) => {
    return isAircraftSectionDraft(aircraft?.id, section);
  };
  const isEnabledDelivery = (section, aircraft) => {
    return isDeliverySectionDraft(aircraft?.id, section);
  };
  const isEnabledWarranty = (section, aircraft) => {
    return isWarrantySectionDraft(aircraft?.id, section);
  };
  const isEnabledBalance = (section, aircraft) => {
    return isBalanceSectionDraft(aircraft?.id, section);
  };
  const onOrderChange = (order) => {
    if (!didMount.current) {
      didMount.current = true; // Evita la primera ejecución
      return;
    }
    setSortingOrder(order);
  };

  const onDeleteAircraft = (aircraftId) => {
    openDialog({
      component: DeleteAircraftDialog,
      props: {
        aircraft: idxList[aircraftId],
        close: closeDialog,
        onDelete: () => deleteAircraftAction(aircraftId),
      },
    });
  };
  return (
    <div className="aircraft-list" ref={listRef} onScroll={handleScroll}>
      <HierarchyLines
        idx={-1}
        displayBullet={true}
        totalLength={aircraftList.length}
        totalLines={10}
        noChild={false}
        sortingLine={true}
        initialTop={78}
        depth={0.04}
      ></HierarchyLines>
      <Sorting
        onOrderChange={onOrderChange}
        displayButton={false}
        showHeaderOrder={true}
        properties={{
          operationalStatus: "Status",
          companyId: "Company",
          tailNumber: "Tail Number",
          registrationNumber: "Registration Number",
          serialNumber: "Serial Number",
          lineNumber: "Line Number",
        }}
      />
      {aircraftList.map(
        (aircraft, idx) =>
          aircraft &&
          idxList[aircraft.id] && (
            <React.Fragment key={`${aircraft.id}-${idx}`}>
              <div
                className={`parent-container-item ${
                  openStates[aircraft.id] ? "active-class-parent" : ""
                }`}
              >
                <HierarchyLines
                  idx={idx}
                  displayBullet={true}
                  totalLength={aircraftList.length}
                  totalLines={13}
                ></HierarchyLines>

                <Item
                  onClick={() => toggleOpenState(aircraft.id)}
                  orderedProperties={[
                    "companyName",
                    "tailNumber",
                    "registrationNumber",
                    "serialNumber",
                    "lineNumber",
                  ]}
                  hideLabels={true}
                  lightFont={true}
                  propertiesPlaceholder={{ ata: "00-00-00" }}
                  labelMapper={mapper}
                  valuePlaceholder="--"
                  ignoreProperties={[
                    "id",
                    "ATAChapter",
                    "ATASection",
                    "ATAParagraph",
                    "partNumber",
                    "description",
                    "categoryName",
                    "subCategoryName",
                  ]}
                  ribbon={[
                    {
                      label: LABELS.DRAFT,
                      when:
                        isAircraftDraft(aircraft.id) ||
                        isDeliveryDraft(aircraft.id) ||
                        isWarrantyDraft(aircraft.id) ||
                        isBalanceDraft(aircraft.id),
                    },
                  ]}
                  itemPrefix={() => (
                    <div className="aircraft-bullet-container">
                      {idxList[aircraft.id].operationalStatus == "Active" && (
                        <div className="aircraft-bullet-container-item bullet-fix active">
                          <div className="icon-flight"></div>
                          <span>Active</span>
                        </div>
                      )}

                      {idxList[aircraft.id].operationalStatus == "Inactive" && (
                        <div className="aircraft-bullet-container-item bullet-fix inactive">
                          <div className="icon-flight"></div>
                          <span>Inactive</span>
                        </div>
                      )}

                      {idxList[aircraft.id].operationalStatus == "Hold" && (
                        <div className="aircraft-bullet-container-item bullet-fix hold">
                          <div className="icon-flight"></div>
                          <span>Hold</span>
                        </div>
                      )}
                    </div>
                  )}
                  item={idxList[aircraft.id]}
                  itemSuffix={() => (
                    <div
                      className="button-actions-container"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {hasPermission("delete") && (
                        <Tooltip
                          className="custom-tooltip"
                          title={"Delete Aircraft"}
                          arrow
                        >
                          <button
                            className="delete-part-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteAircraft(aircraft?.id);
                            }}
                          >
                            <i className="icon-delete-new"></i>
                          </button>{" "}
                        </Tooltip>
                      )}
                    </div>
                  )}
                />
                {openStates[aircraft.id] && (
                  <div className="general-aircraft-form-container">
                    <div className="forms-aircraft-container">
                      <AircraftForm
                        disabled={isEnabled("general", aircraft)}
                        aircraft={aircraft}
                        open={openStates[aircraft.id]}
                        ribbon={[
                          {
                            label: LABELS.DRAFT,
                            when: isAircraftDraft(aircraft.id),
                          },
                        ]}
                      ></AircraftForm>
                      <AircraftFlightStatus
                        aircraft={aircraft}
                        open={openStates[aircraft.id]}
                      ></AircraftFlightStatus>
                    </div>
                    <div className="forms-aircraft-container-single">
                      <AircraftSeatConfiguration></AircraftSeatConfiguration>
                    </div>
                    <div className="forms-aircraft-container">
                      <AircraftDeliveryInformation
                        disabled={isEnabledDelivery("general", aircraft)}
                        aircraft={aircraft}
                        numberOfEngines={numberOfEngines}
                        numberOfLandingGears={numberOfLandingGears}
                        open={openStates[aircraft.id]}
                        ribbon={[
                          {
                            label: LABELS.DRAFT,
                            when: isDeliveryDraft(aircraft.id),
                          },
                        ]}
                      ></AircraftDeliveryInformation>
                      <div className="forms-warranty-container">
                        <AircraftWarrantyConfiguration
                          disabled={isEnabledWarranty("general", aircraft)}
                          aircraft={aircraft}
                          open={openStates[aircraft.id]}
                          ribbon={[
                            {
                              label: LABELS.DRAFT,
                              when: isWarrantyDraft(aircraft.id),
                            },
                          ]}
                        ></AircraftWarrantyConfiguration>
                        <AircraftBalanceConfiguration
                          disabled={isEnabledBalance("general", aircraft)}
                          aircraft={aircraft}
                          open={openStates[aircraft.id]}
                          ribbon={[
                            {
                              label: LABELS.DRAFT,
                              when: isBalanceDraft(aircraft.id),
                            },
                          ]}
                        ></AircraftBalanceConfiguration>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          )
      )}
    </div>
  );
};

export default AircraftList;
